/**
 * Promotion Engine - Utility Helpers
 * Shared functions for date checking, currency formatting, and cart filtering.
 */
class LcPromotionUtils {
  /**
   * Check if the offer is active within scheduling parameters.
   * @param {string} startISO - Start Date ISO String
   * @param {string} endISO - End Date ISO String
   * @returns {boolean}
   */
  static isScheduledActive(startISO, endISO) {
    const now = new Date();
    if (startISO) {
      const startDate = new Date(startISO);
      if (now < startDate) return false;
    }
    if (endISO) {
      const endDate = new Date(endISO);
      if (now > endDate) return false;
    }
    return true;
  }

  /**
   * Check if a specific cart item matches products or collection constraints.
   * @param {object} item - Shopify Cart line item object
   * @param {Array<string>} targetProductHandles - List of target product handles
   * @param {string} targetCollectionHandle - Target collection handle
   * @param {Array<string>} excludedProductHandles - List of excluded product handles
   * @returns {boolean}
   */
  static isItemMatching(item, targetProductHandles = [], targetCollectionHandle = '', excludedProductHandles = []) {
    // 1. Exclusions check
    if (excludedProductHandles && excludedProductHandles.length > 0 && excludedProductHandles.includes(item.handle)) {
      return false;
    }

    // 2. Target products check
    if (targetProductHandles && targetProductHandles.length > 0) {
      return targetProductHandles.includes(item.handle);
    }

    // 3. Target collection check
    if (targetCollectionHandle) {
      const collections = window.LcCartProductCollections?.[item.handle] || item.collection_handles || [];
      return collections.includes(targetCollectionHandle);
    }

    // If no target products or collections are specified, it applies to everything (sitewide)
    return true;
  }

  /**
   * Helper to check if a cart item is an auto-added promo free gift.
   */
  static isFreeGift(item) {
    if (!item || !item.properties) return false;
    if (Array.isArray(item.properties)) {
      return item.properties.some(p => (Array.isArray(p) && p[0] === '_is_free_gift' && p[1] === 'true') || (p && p.name === '_is_free_gift' && p.value === 'true'));
    } else if (typeof item.properties === 'object') {
      return item.properties['_is_free_gift'] === 'true' || item.properties['_is_free_gift'] === true;
    }
    return false;
  }

  /**
   * Calculate qualifying total quantity of target products in the cart.
   * @param {object} cart - Shopify Cart object
   * @param {object} promo - Promotion metadata
   * @returns {number}
   */
  static getQualifyingQuantity(cart, promo) {
    if (!cart || !cart.items) return 0;
    return cart.items.reduce((total, item) => {
      // Do not count free gift items towards qualifying quantities!
      if (this.isFreeGift(item)) return total;

      if (this.isItemMatching(item, promo.targetProducts, promo.targetCollection, promo.excludedProducts)) {
        return total + item.quantity;
      }
      return total;
    }, 0);
  }

  /**
   * Calculate qualifying subtotal value of target products in the cart.
   * @param {object} cart - Shopify Cart object
   * @param {object} promo - Promotion metadata
   * @returns {number} - Subtotal in cents
   */
  static getQualifyingSubtotal(cart, promo) {
    if (!cart || !cart.items) return 0;
    return cart.items.reduce((total, item) => {
      // Do not count free gift items towards qualifying subtotal!
      if (this.isFreeGift(item)) return total;

      if (this.isItemMatching(item, promo.targetProducts, promo.targetCollection, promo.excludedProducts)) {
        return total + (item.quantity * item.original_price);
      }
      return total;
    }, 0);
  }

  /**
   * Formats a price in cents using Shopify shop format rules.
   * @param {number} cents
   * @returns {string}
   */
  static formatMoney(cents) {
    const formatString = window.LcSettings?.moneyFormat || '₹{{amount}}';
    let value = (cents / 100).toFixed(2);
    
    // Clean trailing zeros if appropriate or format accordingly
    if (formatString.includes('no_decimals')) {
      value = Math.round(cents / 100).toString();
    }
    
    return formatString.replace(/\{\{\s*amount\s*\}\}/g, value)
                       .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, Math.round(cents / 100).toString());
  }
}

window.LcPromotionUtils = LcPromotionUtils;
